import * as i0 from '@angular/core';
import { Directive, InjectionToken, inject, Injectable, ViewContainerRef, ComponentFactoryResolver, Component, HostListener, ViewChild, Injector, ApplicationRef, ElementRef, Renderer2, RendererStyleFlags2, Input, TemplateRef, ɵstringify as _stringify, NgModule, importProvidersFrom, makeEnvironmentProviders } from '@angular/core';
import { Router } from '@angular/router';
import { ReplaySubject, BehaviorSubject, combineLatest, of, Subject, Observable, EMPTY } from 'rxjs';
import { first, tap, switchMap, map, catchError, retry } from 'rxjs/operators';
import { AccountSettingsService } from '@tc/account-settings';
import { PermissionService } from '@tc/permissions-legacy';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { EnvironmentService } from '@tc/foundation';
import { LoggerService, Severity } from '@tc/logger';
import { AuthenticationService } from '@tc/security-oauth-legacy';
import { AsyncPipe, NgClass } from '@angular/common';

/**
 * @deprecated Remove from project
 */
function MockTranslationDirective() {
    const metadata = {
        selector: '[translation]',
        inputs: ['translation', 'translationForceForward'],
    };
    return Directive(metadata)(class MockTranslationDirectiveClass {
    });
}

/**
 * Injection token for specifying the translation group name.
 * Used to organize translations into logical groups (e.g., 'App', 'Admin', 'PublicSite').
 */
const TranslationGroupNameToken = new InjectionToken('TranslationGroupNameToken');
/**
 * Injection token for specifying the CSS class to apply to translation indicators.
 * Used to customize the visual appearance of translatable elements in translation mode.
 */
const TranslationIndicatorClassToken = new InjectionToken('TranslationIndicatorClassToken');

// Language Codes From: https://www.metamodpro.com/browser-language-codes
/**
 * A collection of language constants represented as key-value pairs.
 * Each key represents a language or language variant, and the associated value is its corresponding language code, typically adhering to ISO 639 or related standards.
 *
 * LanguageType is a utility object designed to map languages to their codes for localization, translation, and other internationalization purposes.
 *
 * Fields:
 * - The keys represent languages, regional variants, or specific dialects, formatted in camel case.
 * - The values are two-letter, three-letter, or extended codes that uniquely identify the language or its locale variant.
 */
const LanguageType = {
    NoLanguage: '',
    Afrikaans: 'af',
    Albanian: 'sq',
    ArabicStandard: 'ar',
    ArabicAlgeria: 'ar-dz',
    ArabicBahrain: 'ar-bh',
    ArabicEgypt: 'ar-eg',
    ArabicIraq: 'ar-iq',
    ArabicJordan: 'ar-jo',
    ArabicKuwait: 'ar-kw',
    ArabicLebanon: 'ar-lb',
    ArabicLibya: 'ar-ly',
    ArabicMorocco: 'ar-ma',
    ArabicOman: 'ar-om',
    ArabicQatar: 'ar-qa',
    ArabicSaudiArabia: 'ar-sa',
    ArabicSyria: 'ar-sy',
    ArabicTunisia: 'ar-tn',
    ArabicUAE: 'ar-ae',
    ArabicYemen: 'ar-ye',
    Aragonese: 'ar',
    Armenian: 'hy',
    Assamese: 'as',
    Asturian: 'ast',
    Azerbaijani: 'az',
    Basque: 'eu',
    Belarusian: 'be',
    Bengali: 'bn',
    Bosnian: 'bs',
    Breton: 'br',
    Bulgarian: 'bg',
    Burmese: 'my',
    Catalan: 'ca',
    Chamorro: 'ch',
    Chechen: 'ce',
    Chinese: 'zh',
    ChineseHongKong: 'zh-hk',
    ChinesePRC: 'zh-cn',
    ChineseSingapore: 'zh-sg',
    ChineseTaiwan: 'zh-tw',
    Chuvash: 'cv',
    Corsican: 'co',
    Cree: 'cr',
    PersianIran: 'fa-ir',
    Croatian: 'hr',
    Czech: 'cs',
    Danish: 'da',
    DutchStandard: 'nl',
    DutchBelgian: 'nl-be',
    English: 'en',
    EnglishAustralia: 'en-au',
    EnglishBelize: 'en-bz',
    EnglishCanada: 'en-ca',
    EnglishIreland: 'en-ie',
    EnglishJamaica: 'en-jm',
    EnglishNewZealand: 'en-nz',
    EnglishPhilippines: 'en-ph',
    EnglishSouthAfrica: 'en-za',
    EnglishTrinidadTobago: 'en-tt',
    EnglishUnitedKingdom: 'en-gb',
    EnglishUnitedStates: 'en-us',
    EnglishZimbabwe: 'en-zw',
    Esperanto: 'eo',
    Estonian: 'et',
    Faeroese: 'fo',
    Farsi: 'fa',
    Fijian: 'fj',
    Finnish: 'fi',
    FrenchStandard: 'fr',
    FrenchBelgium: 'fr-be',
    FrenchCanada: 'fr-ca',
    FrenchFrance: 'fr-fr',
    FrenchLuxembourg: 'fr-lu',
    FrenchMonaco: 'fr-mc',
    FrenchSwitzerland: 'fr-ch',
    Frisian: 'fy',
    Friulian: 'fur',
    GaelicScots: 'gd',
    GaelicIrish: 'gd-ie',
    Galacian: 'gl',
    Georgian: 'ka',
    GermanStandard: 'de',
    GermanAustria: 'de-at',
    GermanGermany: 'de-de',
    GermanLiechtenstein: 'de-li',
    GermanLuxembourg: 'de-lu',
    GermanSwitzerland: 'de-ch',
    SpanishVenezuela: 'es-ve',
    Greek: 'el',
    Gujurati: 'gu',
    Haitian: 'ht',
    Hebrew: 'he',
    Hindi: 'hi',
    Hungarian: 'hu',
    Icelandic: 'is',
    Indonesian: 'id',
    Inuktitut: 'iu',
    Irish: 'ga',
    ItalianStandard: 'it',
    ItalianSwitzerland: 'it-ch',
    Japanese: 'ja',
    Kannada: 'kn',
    Kashmiri: 'ks',
    Kazakh: 'kk',
    Khmer: 'km',
    Kirghiz: 'ky',
    Klingon: 'tlh',
    Korean: 'ko',
    KoreanNorthKorea: 'ko-kp',
    KoreanSouthKorea: 'ko-kr',
    Latin: 'la',
    Latvian: 'lv',
    Lithuanian: 'lt',
    Luxembourgish: 'lb',
    FYROMacedonian: 'mk',
    Malay: 'ms',
    Malayalam: 'ml',
    Maltese: 'mt',
    Maori: 'mi',
    Marathi: 'mr',
    Moldavian: 'mo',
    Navajo: 'nv',
    Ndonga: 'ng',
    Nepali: 'ne',
    Norwegian: 'no',
    NorwegianBokmal: 'nb',
    NorwegianNynorsk: 'nn',
    Occitan: 'oc',
    Oriya: 'or',
    Oromo: 'om',
    Persian: 'fa',
    Polish: 'pl',
    Portuguese: 'pt',
    PortugueseBrazil: 'pt-br',
    Punjabi: 'pa',
    PunjabiIndia: 'pa-in',
    PunjabiPakistan: 'pa-pk',
    Quechua: 'qu',
    RhaetoRomanic: 'rm',
    Romanian: 'ro',
    RomanianMoldavia: 'ro-mo',
    Russian: 'ru',
    RussianMoldavia: 'ru-mo',
    SamiLappish: 'sz',
    Sango: 'sg',
    Sanskrit: 'sa',
    Sardinian: 'sc',
    ScotsGaelic: 'gd',
    Sindhi: 'sd',
    Singhalese: 'si',
    Serbian: 'sr',
    Slovak: 'sk',
    Slovenian: 'sl',
    Somani: 'so',
    Sorbian: 'sb',
    Spanish: 'es',
    SpanishArgentina: 'es-ar',
    SpanishBolivia: 'es-bo',
    SpanishChile: 'es-cl',
    SpanishColombia: 'es-co',
    SpanishCostaRica: 'es-cr',
    SpanishDominicanRepublic: 'es-do',
    SpanishEcuador: 'es-ec',
    SpanishElSalvador: 'es-sv',
    SpanishGuatemala: 'es-gt',
    SpanishHonduras: 'es-hn',
    SpanishMexico: 'es-mx',
    SpanishNicaragua: 'es-ni',
    SpanishPanama: 'es-pa',
    SpanishParaguay: 'es-py',
    SpanishPeru: 'es-pe',
    SpanishPuertoRico: 'es-pr',
    SpanishSpain: 'es-es',
    SpanishUruguay: 'es-uy',
    Sutu: 'sx',
    Swahili: 'sw',
    Swedish: 'sv',
    SwedishFinland: 'sv-fi',
    SwedishSweden: 'sv-sv',
    Tamil: 'ta',
    Tatar: 'tt',
    Teluga: 'te',
    Thai: 'th',
    Tigre: 'tig',
    Tsonga: 'ts',
    Tswana: 'tn',
    Turkish: 'tr',
    Turkmen: 'tk',
    Ukrainian: 'uk',
    UpperSorbian: 'hsb',
    Urdu: 'ur',
    Venda: 've',
    Vietnamese: 'vi',
    Volapuk: 'vo',
    Walloon: 'wa',
    Welsh: 'cy',
    Xhosa: 'xh',
    Yiddish: 'ji',
    Zulu: 'zu',
};

/**
 * Service that manages language selection and availability for the translation system.
 * This service is provided at root level and shared across the entire application.
 *
 * Responsibilities:
 * - Detecting and initializing the user's preferred language
 * - Managing language changes
 * - Controlling translation mode state
 * - Determining user permissions for translation editing
 *
 * @example
 * ```typescript
 * // Inject the service
 * constructor(private languageService: LanguageService) {}
 *
 * // Subscribe to language changes
 * this.languageService.language$.subscribe((language) => {
 *   console.log('Current language:', language);
 * });
 *
 * // Change the language
 * this.languageService.changeLanguage('fr');
 *
 * // Enable translation mode
 * this.languageService.setIsTranslating(true);
 * ```
 */
class LanguageService {
    constructor() {
        this.http = inject(HttpClient);
        this.envService = inject(EnvironmentService);
        this.accountSettingsService = inject(AccountSettingsService);
        this.authService = inject(AuthenticationService);
        this.permissionService = inject(PermissionService);
        this.loggerService = inject(LoggerService);
        this.CanTranslatePrefix = 'CanTranslate-';
        this.availableLanguagesSubject = new ReplaySubject(1);
        /**
         * Observable that emits the list of available active languages.
         * Updated when translation mode is enabled/disabled or languages are reloaded.
         *
         * @example
         * ```typescript
         * this.languageService.availableLanguages$.subscribe((languages) => {
         *   this.languageOptions = languages.map(l => ({
         *     value: l.code,
         *     label: l.name
         *   }));
         * });
         * ```
         */
        this.availableLanguages$ = this.availableLanguagesSubject.asObservable();
        this.languageSubject = new ReplaySubject(1);
        /**
         * Observable that emits the currently selected language code.
         * This is the language used for displaying translations throughout the app.
         *
         * @example
         * ```typescript
         * this.languageService.language$.subscribe((language) => {
         *   document.documentElement.lang = language;
         * });
         * ```
         */
        this.language$ = this.languageSubject.asObservable();
        this.languageChangedSubject = new ReplaySubject(1);
        /**
         * Observable that emits whenever the language is changed by the user or system.
         * Use this to trigger translation reloads or update language-dependent logic.
         *
         * @example
         * ```typescript
         * this.languageService.languageChanged$.subscribe((newLanguage) => {
         *   this.analytics.trackLanguageChange(newLanguage);
         *   this.reloadUserPreferences();
         * });
         * ```
         */
        this.languageChanged$ = this.languageChangedSubject.asObservable();
        this.isTranslatingSubject = new BehaviorSubject(false);
        /**
         * Observable that emits the current translation mode state.
         * When true, users can click on translations to edit them.
         *
         * @example
         * ```typescript
         * this.languageService.isTranslating$.subscribe((isTranslating) => {
         *   this.showTranslationTools = isTranslating;
         * });
         * ```
         */
        this.isTranslating$ = this.isTranslatingSubject.asObservable();
        this.canTranslateSubject = new ReplaySubject(1);
        /**
         * Observable that emits whether the current user has permission to translate.
         * Based on authentication status and 'CanTranslate-*' permissions.
         *
         * @example
         * ```typescript
         * this.languageService.canTranslate$.subscribe((canTranslate) => {
         *   this.showTranslationModeToggle = canTranslate;
         * });
         * ```
         */
        this.canTranslate$ = this.canTranslateSubject.asObservable();
        this.languagePermissionSubscription = combineLatest([
            this.authService.isAuthenticated$,
            this.permissionService.permissions$,
        ]).subscribe(([isAuthed, permissions]) => {
            this.canTranslateSubject.next(isAuthed &&
                permissions.some((p) => p.startsWith(this.CanTranslatePrefix)));
        });
    }
    ngOnDestroy() {
        if (this.languagePermissionSubscription) {
            this.languagePermissionSubscription.unsubscribe();
        }
    }
    /**
     * Toggles translation mode on or off.
     * Translation mode allows users with proper permissions to click on translations and edit them.
     * When enabled, reloads available languages to include all languages (not just active ones).
     *
     * @param isTranslating - Whether to enable (true) or disable (false) translation mode
     *
     * @example
     * ```typescript
     * // Enable translation mode
     * this.languageService.setIsTranslating(true);
     *
     * // Disable translation mode
     * this.languageService.setIsTranslating(false);
     * ```
     *
     * @example
     * ```typescript
     * // Toggle translation mode based on checkbox
     * onTranslationModeToggle(checked: boolean): void {
     *   this.languageService.setIsTranslating(checked);
     * }
     * ```
     */
    setIsTranslating(isTranslating) {
        this.canTranslate$.pipe(first()).subscribe((canTranslate) => {
            this.isTranslatingSubject.next(canTranslate && isTranslating);
            this.initLanguage().subscribe();
        });
    }
    /**
     * This is a finite observable, and it should only be called externally by APP_INITIALIZER.
     */
    initLanguage() {
        return this.getLanguages().pipe(tap((languages) => this.availableLanguagesSubject.next(languages)), switchMap((languages) => this.accountSettingsService.settings$.pipe(first(), map((settings) => {
            const defaultNavigatorLanguage = navigator.languages?.[0] ??
                navigator.language ??
                LanguageType.EnglishUnitedStates;
            let languageCode = settings.DisplayLanguage == null ||
                settings.DisplayLanguage === LanguageType.NoLanguage
                ? defaultNavigatorLanguage
                : settings.DisplayLanguage;
            if (languageCode == null ||
                languageCode === LanguageType.NoLanguage ||
                languageCode.startsWith('en')) {
                languageCode = this.handleLanguageExistence(languages, languageCode);
                this.languageSubject.next(languageCode);
                this.languageChangedSubject.next(languageCode);
            }
            else if (languageCode === 'zh') {
                languageCode = languages.some((l) => l.code === LanguageType.ChineseTaiwan)
                    ? LanguageType.ChineseTaiwan
                    : languages.some((l) => l.code === LanguageType.ChinesePRC)
                        ? LanguageType.ChinesePRC
                        : LanguageType.EnglishUnitedStates;
                this.languageSubject.next(languageCode);
                this.languageChangedSubject.next(languageCode);
            }
            else if (languageCode.toLowerCase().startsWith('zh-hans')) {
                languageCode = LanguageType.ChinesePRC;
                this.languageSubject.next(languageCode);
                this.languageChangedSubject.next(languageCode);
            }
            else if (languageCode.toLowerCase().startsWith('zh-hant')) {
                languageCode = LanguageType.ChineseTaiwan;
                this.languageSubject.next(languageCode);
                this.languageChangedSubject.next(languageCode);
            }
            else if (languages.findIndex((l) => l.code === languageCode.toLowerCase()) === -1) {
                languageCode = languages[0].code;
                this.languageSubject.next(languageCode);
                this.languageChangedSubject.next(languageCode);
            }
            else {
                languageCode = languageCode.toLowerCase();
                this.languageSubject.next(languageCode);
            }
            this.accountSettingsService.set('DisplayLanguage', languageCode);
        }))));
    }
    handleLanguageExistence(languages, languageCode) {
        let updatedLanguageCode = languageCode;
        if (languages.length > 0 &&
            languages.findIndex((l) => l.code === languageCode.toLowerCase()) === -1) {
            updatedLanguageCode = languages[0].code;
        }
        return updatedLanguageCode.toLowerCase();
    }
    /**
     * Changes the current language.
     *
     * @param language The LanguageType to be set.
     */
    changeLanguage(languageType) {
        this.accountSettingsService.set('DisplayLanguage', languageType);
        this.languageSubject.next(languageType);
        this.languageChangedSubject.next(languageType);
    }
    /**
     * Finite observable that retrieves a list of active languages. If a user has any kind of
     * CanTranslate- prefixed permission and isTranslating is true, then the Translator endpoint
     * is used to obtain a list of languages. If not, the regular getLanguages endpoint is used.
     */
    getLanguages() {
        return combineLatest([
            this.envService.configuration$,
            this.isTranslating$,
        ]).pipe(first(), switchMap(([configuration, isTranslating]) => {
            const apiUrl = configuration.translatorApiUrl ?? configuration.apiUrl;
            const endpoint = isTranslating
                ? configuration.getTranslatorLanguagesEndpoint
                : configuration.getLanguagesEndpoint;
            const url = `${apiUrl}/${endpoint}`;
            let params = new HttpParams();
            params = params.set('ApplicationName', configuration.applicationName);
            return this.http.get(url, { params }).pipe(catchError((error) => {
                this.loggerService.log('LanguageService.getLanguages', Severity.Error, this.createLogData(error));
                return of([]);
            }));
        }));
    }
    createLogData(httpError) {
        const logData = {
            message: httpError.message,
            name: httpError.name,
            status: httpError.status,
            statusText: httpError.statusText,
            url: httpError.url ?? undefined,
            ok: httpError.ok,
            error: httpError.error,
        };
        return logData;
    }
    static { this.ɵfac = function LanguageService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || LanguageService)(); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: LanguageService, factory: LanguageService.ɵfac, providedIn: 'root' }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(LanguageService, [{
        type: Injectable,
        args: [{ providedIn: 'root' }]
    }], () => [], null); })();

/**
 * An enumeration representing the translation status of a given element.
 *
 * This enum is used to define the status of translation for items or objects
 * that require localization or translation into different languages.
 */
var TranslationStatusType;
(function (TranslationStatusType) {
    TranslationStatusType["Translated"] = "Translated";
    TranslationStatusType["RequireTranslation"] = "RequireTranslation";
})(TranslationStatusType || (TranslationStatusType = {}));

class ModalContentDirective {
    constructor() {
        this.viewContainerRef = inject(ViewContainerRef);
    }
    static { this.ɵfac = function ModalContentDirective_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || ModalContentDirective)(); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: ModalContentDirective, selectors: [["", "modalContent", ""]] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ModalContentDirective, [{
        type: Directive,
        args: [{
                standalone: true,
                // eslint-disable-next-line @angular-eslint/directive-selector
                selector: '[modalContent]',
            }]
    }], null, null); })();

function OverlayComponent_ng_template_1_Template(rf, ctx) { }
class OverlayComponent {
    constructor() {
        this.componentFactoryResolver = inject(ComponentFactoryResolver);
        this.requestCloseSubscription = null;
        this.modalComponentRef = null;
        this.closeOnOverlayClick = true;
        this.requestCloseSubject = new Subject();
        this.RequestClose$ = this.requestCloseSubject.asObservable();
        this.hardCloseSubject = new Subject();
        this.HardClose$ = this.hardCloseSubject.asObservable();
        this.component = null;
    }
    onClick(event) {
        const modalElementCandidate = this.modalComponentRef?.location.nativeElement;
        const target = event.target;
        if (this.closeOnOverlayClick &&
            modalElementCandidate instanceof Node &&
            target instanceof Node &&
            !modalElementCandidate.contains(target)) {
            event.stopPropagation();
            event.preventDefault();
            this.hardCloseSubject.next();
        }
    }
    set Component(component) {
        if (this.component != null) {
            throw new Error('The Component property has already been set.');
        }
        this.component = component;
        this.setupContent();
    }
    get componentInstance() {
        if (this.modalComponentRef == null) {
            throw new Error('The modal component has not been initialized.');
        }
        return this.modalComponentRef;
    }
    ngOnDestroy() {
        this.requestCloseSubscription?.unsubscribe();
    }
    applyModalConfiguration(configuration) {
        if (this.modalComponentRef?.instance == null) {
            throw new Error('Cannot apply configuration until Component property is set.');
        }
        this.closeOnOverlayClick = configuration.closeOnOverlayClick ?? true;
        if ('data' in configuration) {
            this.modalComponentRef.instance.data = configuration.data;
        }
        this.modalComponentRef.changeDetectorRef.detectChanges();
    }
    raiseValidationError(response) {
        if (this.modalComponentRef == null) {
            throw new Error('The modal component has not been initialized.');
        }
        this.modalComponentRef.instance.raiseValidationError(response);
    }
    setupContent() {
        if (this.component != null) {
            const componentFactory = this.componentFactoryResolver.resolveComponentFactory(this.component);
            this.contentContainer.viewContainerRef.clear();
            this.modalComponentRef =
                this.contentContainer.viewContainerRef.createComponent(componentFactory);
            this.setupCloseListeners();
        }
    }
    setupCloseListeners() {
        if (this.modalComponentRef?.instance != null) {
            this.modalComponentRef.instance.HardClose$.pipe(first()).subscribe(() => this.hardCloseSubject.next());
            this.requestCloseSubscription =
                this.modalComponentRef.instance.RequestClose$.subscribe((data) => this.requestCloseSubject.next(data));
        }
    }
    static { this.ɵfac = function OverlayComponent_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || OverlayComponent)(); }; }
    static { this.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: OverlayComponent, selectors: [["tc-overlay"]], viewQuery: function OverlayComponent_Query(rf, ctx) { if (rf & 1) {
            i0.ɵɵviewQuery(ModalContentDirective, 7);
        } if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.contentContainer = _t.first);
        } }, hostBindings: function OverlayComponent_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("click", function OverlayComponent_click_HostBindingHandler($event) { return ctx.onClick($event); });
        } }, decls: 2, vars: 0, consts: [[1, "overlay"], ["modalContent", ""]], template: function OverlayComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵelement(0, "div", 0);
            i0.ɵɵtemplate(1, OverlayComponent_ng_template_1_Template, 0, 0, "ng-template", 1);
        } }, dependencies: [ModalContentDirective], styles: ["[_nghost-%COMP%]{align-items:center;display:flex;flex-direction:column;height:100vh;justify-content:center;left:0;position:fixed;top:0;width:100vw;z-index:9999}.overlay[_ngcontent-%COMP%]{background-color:#0009;height:100vh;position:absolute;width:100vw}"] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(OverlayComponent, [{
        type: Component,
        args: [{ selector: 'tc-overlay', standalone: true, imports: [ModalContentDirective], template: "<div class=\"overlay\"></div>\n<ng-template modalContent></ng-template>\n", styles: [":host{align-items:center;display:flex;flex-direction:column;height:100vh;justify-content:center;left:0;position:fixed;top:0;width:100vw;z-index:9999}.overlay{background-color:#0009;height:100vh;position:absolute;width:100vw}\n"] }]
    }], null, { contentContainer: [{
            type: ViewChild,
            args: [ModalContentDirective, { static: true }]
        }], onClick: [{
            type: HostListener,
            args: ['click', ['$event']]
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(OverlayComponent, { className: "OverlayComponent", filePath: "lib/dialog/Overlay.component.ts", lineNumber: 15 }); })();

class DomService {
    constructor() {
        this.componentFactoryResolver = inject(ComponentFactoryResolver);
        this.injector = inject(Injector);
        this.id = 0;
        this.componentRefs = {};
    }
    get appRef() {
        return this.injector.get(ApplicationRef);
    }
    appendComponentToBody(component) {
        const componentFactory = this.componentFactoryResolver.resolveComponentFactory(component);
        const componentRef = componentFactory.create(this.injector);
        this.appRef.attachView(componentRef.hostView);
        const hostView = componentRef.hostView;
        const rootNodes = hostView.rootNodes;
        const [rootNode] = rootNodes;
        if (rootNode instanceof HTMLElement) {
            document.body.appendChild(rootNode);
        }
        else if (componentRef.location.nativeElement instanceof HTMLElement) {
            document.body.appendChild(componentRef.location.nativeElement);
        }
        this.id++;
        this.componentRefs[this.id] = componentRef;
        return this.id;
    }
    getComponent(id) {
        const componentRef = this.componentRefs[id];
        return componentRef ?? null;
    }
    removeComponentFromBody(id) {
        const componentRef = this.componentRefs[id];
        if (!componentRef) {
            return;
        }
        this.appRef.detachView(componentRef.hostView);
        componentRef.destroy();
        delete this.componentRefs[id];
    }
    static { this.ɵfac = function DomService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || DomService)(); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: DomService, factory: DomService.ɵfac, providedIn: 'root' }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DomService, [{
        type: Injectable,
        args: [{ providedIn: 'root' }]
    }], null, null); })();

class ModalService {
    constructor() {
        this.domService = inject(DomService);
        this.modals = {};
        this.requestCloseSubscription = null;
        this.hardCloseSubscription = null;
        this.validateBeforeClosing = null;
        this.whenClosed = null;
    }
    ngOnDestroy() {
        this.requestCloseSubscription?.unsubscribe();
        this.hardCloseSubscription?.unsubscribe();
    }
    showModal(content, configuration) {
        const refId = this.domService.appendComponentToBody(OverlayComponent);
        this.modals[refId] = this.domService.getComponent(refId);
        this.modals[refId].instance.Component = content;
        if (configuration == null) {
            this.modals[refId].instance.RequestClose$.pipe(first()).subscribe(() => this.domService.removeComponentFromBody(refId));
            this.modals[refId].instance.HardClose$.pipe(first()).subscribe(() => this.domService.removeComponentFromBody(refId));
        }
        else {
            this.modals[refId].instance.applyModalConfiguration(configuration);
            this.configureEvents(refId, configuration);
        }
        return refId;
    }
    configureEvents(refId, configuration) {
        const modalRef = this.modals[refId];
        if (modalRef == null) {
            throw new Error('Modal reference does not exist.');
        }
        this.validateBeforeClosing = configuration.validateBeforeClosing ?? null;
        this.whenClosed = configuration.whenClosed ?? null;
        this.hardCloseSubscription = modalRef.instance.HardClose$.pipe(first()).subscribe(() => {
            this.whenClosed?.();
            this.domService.removeComponentFromBody(refId);
        });
        this.requestCloseSubscription = modalRef.instance.RequestClose$.subscribe((data) => {
            if (this.validateBeforeClosing == null) {
                this.whenClosed?.();
                this.domService.removeComponentFromBody(refId);
                return;
            }
            this.validateBeforeClosing(data)
                .pipe(first())
                .subscribe((validationResult) => {
                if (validationResult.isSuccess) {
                    this.whenClosed?.(validationResult);
                    this.domService.removeComponentFromBody(refId);
                }
                else {
                    modalRef.instance.raiseValidationError(validationResult);
                }
            });
        });
    }
    static { this.ɵfac = function ModalService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || ModalService)(); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: ModalService, factory: ModalService.ɵfac, providedIn: 'root' }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ModalService, [{
        type: Injectable,
        args: [{ providedIn: 'root' }]
    }], null, null); })();

class TranslationDialogResult {
    get isSuccess() {
        return (this.error == null &&
            this.failedResultMessages?.length === 0);
    }
    constructor(data, response) {
        this.updated = 0;
        this.inserted = 0;
        this.markedForRetranslation = 0;
        this.failed = 0;
        this.failedResultMessages = [];
        this.data = data;
        if (response instanceof HttpErrorResponse) {
            this.error = response;
        }
        else {
            this.updated = response.updated;
            this.inserted = response.inserted;
            this.markedForRetranslation = response.markedForRetranslation;
            this.failed = response.failed;
            this.failedResultMessages = response.failedResultMessages;
        }
    }
}

class BaseModalComponent {
    constructor() {
        this.hardCloseSubject = new Subject();
        this.HardClose$ = this.hardCloseSubject.asObservable();
        this.requestCloseSubject = new Subject();
        this.RequestClose$ = this.requestCloseSubject.asObservable();
        this.validationErrorSubject = new Subject();
        this.ValidationError$ = this.validationErrorSubject.asObservable();
        this.isValidatingSubject = new BehaviorSubject(false);
        this.IsValidating$ = this.isValidatingSubject.asObservable();
    }
    raiseValidationError(response) {
        this.isValidatingSubject.next(false);
        this.validationErrorSubject.next(response);
    }
    close() {
        this.hardCloseSubject.next();
    }
    validateAndClose() {
        this.isValidatingSubject.next(true);
        this.requestCloseSubject.next(this.data);
    }
    static { this.ɵfac = function BaseModalComponent_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || BaseModalComponent)(); }; }
    static { this.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: BaseModalComponent, selectors: [["tc-base-modal"]], decls: 1, vars: 0, template: function BaseModalComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵdomElement(0, "div");
        } }, encapsulation: 2 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(BaseModalComponent, [{
        type: Component,
        args: [{
                selector: 'tc-base-modal',
                template: '<div></div>',
                standalone: true,
            }]
    }], null, null); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(BaseModalComponent, { className: "BaseModalComponent", filePath: "lib/dialog/base-modal.component.ts", lineNumber: 9 }); })();

const _c0$1 = ["translatedTextArea"];
const _c1 = a0 => ({ hidden: a0 });
function TranslatorDialogComponent_Conditional_23_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵdomElement(0, "div", 12);
} }
class TranslatorDialogComponent extends BaseModalComponent {
    constructor() {
        super(...arguments);
        this.validationResponseSubscription = null;
        this.isErrorHiddenSubject = new BehaviorSubject(true);
        this.IsErrorHidden$ = this.isErrorHiddenSubject.asObservable();
        this.errorSubject = new BehaviorSubject(null);
        this.Error$ = this.errorSubject.asObservable();
        this.translationExistsSubject = new BehaviorSubject(false);
        this.TranslationExists$ = this.translationExistsSubject.asObservable();
    }
    ngOnInit() {
        this.validationResponseSubscription = this.ValidationError$.subscribe((validationError) => {
            this.isErrorHiddenSubject.next(false);
            const httpError = validationError.error;
            if (httpError != null) {
                this.errorSubject.next(`${httpError.status} (${httpError.statusText}): ${httpError.message}`);
            }
            else if (validationError.failedResultMessages.length > 0) {
                this.errorSubject.next(validationError.failedResultMessages.join(', '));
            }
            else {
                this.errorSubject.next('An unknown error occurred.');
            }
        });
        this.updateTranslationExists();
    }
    ngOnDestroy() {
        this.validationResponseSubscription?.unsubscribe();
    }
    copyOriginalText() {
        if (this.data.TranslatedText === '' ||
            this.data.TranslatedText == null) {
            this.data.TranslatedText = this.data.OriginalText;
            this.translatedTextArea.nativeElement.value =
                this.data.OriginalText;
            this.updateTranslationExists();
        }
    }
    translatedTextChanged() {
        this.isErrorHiddenSubject.next(true);
        this.errorSubject.next(null);
        this.data.TranslatedText = this.translatedTextArea.nativeElement.value;
        this.updateTranslationExists();
    }
    save() {
        if (this.translatedTextArea.nativeElement.value.trim() === '') {
            this.errorSubject.next('A Translation Is Required');
            this.isErrorHiddenSubject.next(false);
            return;
        }
        this.validateAndClose();
    }
    updateTranslationExists() {
        this.translationExistsSubject.next(this.data.TranslatedText !== '' && this.data.TranslatedText != null);
    }
    static { this.ɵfac = /*@__PURE__*/ (() => { let ɵTranslatorDialogComponent_BaseFactory; return function TranslatorDialogComponent_Factory(__ngFactoryType__) { return (ɵTranslatorDialogComponent_BaseFactory || (ɵTranslatorDialogComponent_BaseFactory = i0.ɵɵgetInheritedFactory(TranslatorDialogComponent)))(__ngFactoryType__ || TranslatorDialogComponent); }; })(); }
    static { this.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: TranslatorDialogComponent, selectors: [["tc-translator-dialog"]], viewQuery: function TranslatorDialogComponent_Query(rf, ctx) { if (rf & 1) {
            i0.ɵɵviewQuery(_c0$1, 5);
        } if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.translatedTextArea = _t.first);
        } }, features: [i0.ɵɵInheritDefinitionFeature], decls: 27, vars: 21, consts: [["translatedTextArea", ""], [1, "main-container"], [1, "title"], [1, "sub-title"], [1, "original-text-container"], ["readonly", "readonly", 1, "original-text-area"], [1, "double-click"], [1, "translated-text-container"], ["placeholder", "Input translation here...", 1, "translated-text-area", 3, "dblclick", "change"], [1, "error-message"], [1, "button-container"], [1, "save-button", 3, "click"], [1, "spinner"], [1, "cancel-button", 3, "click"]], template: function TranslatorDialogComponent_Template(rf, ctx) { if (rf & 1) {
            const _r1 = i0.ɵɵgetCurrentView();
            i0.ɵɵdomElementStart(0, "div", 1)(1, "div", 2);
            i0.ɵɵtext(2, "Translation Editor");
            i0.ɵɵdomElementEnd();
            i0.ɵɵdomElementStart(3, "div", 3);
            i0.ɵɵtext(4);
            i0.ɵɵdomElementEnd();
            i0.ɵɵdomElementStart(5, "div", 4)(6, "textarea", 5);
            i0.ɵɵtext(7);
            i0.ɵɵdomElementEnd()();
            i0.ɵɵdomElementStart(8, "div")(9, "span", 6);
            i0.ɵɵpipe(10, "async");
            i0.ɵɵtext(11, " (Double-Click Below to Copy From Above) ");
            i0.ɵɵdomElementEnd()();
            i0.ɵɵdomElementStart(12, "div", 7)(13, "textarea", 8, 0);
            i0.ɵɵdomListener("dblclick", function TranslatorDialogComponent_Template_textarea_dblclick_13_listener() { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.copyOriginalText()); })("change", function TranslatorDialogComponent_Template_textarea_change_13_listener() { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.translatedTextChanged()); });
            i0.ɵɵtext(15);
            i0.ɵɵdomElementEnd()();
            i0.ɵɵdomElementStart(16, "div", 9);
            i0.ɵɵpipe(17, "async");
            i0.ɵɵtext(18);
            i0.ɵɵpipe(19, "async");
            i0.ɵɵdomElementEnd();
            i0.ɵɵdomElementStart(20, "div", 10)(21, "button", 11);
            i0.ɵɵdomListener("click", function TranslatorDialogComponent_Template_button_click_21_listener() { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.save()); });
            i0.ɵɵtext(22, "Save");
            i0.ɵɵdomElementEnd();
            i0.ɵɵconditionalCreate(23, TranslatorDialogComponent_Conditional_23_Template, 1, 0, "div", 12);
            i0.ɵɵpipe(24, "async");
            i0.ɵɵdomElementStart(25, "button", 13);
            i0.ɵɵdomListener("click", function TranslatorDialogComponent_Template_button_click_25_listener() { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.close()); });
            i0.ɵɵtext(26, "Cancel");
            i0.ɵɵdomElementEnd()()();
        } if (rf & 2) {
            i0.ɵɵadvance(4);
            i0.ɵɵtextInterpolate1("Translation Group: ", ctx.data.GroupName);
            i0.ɵɵadvance(3);
            i0.ɵɵtextInterpolate(ctx.data.OriginalText);
            i0.ɵɵadvance(2);
            i0.ɵɵclassMap(i0.ɵɵpureFunction1(17, _c1, i0.ɵɵpipeBind1(10, 9, ctx.TranslationExists$)));
            i0.ɵɵadvance(6);
            i0.ɵɵtextInterpolate(ctx.data.TranslatedText);
            i0.ɵɵadvance();
            i0.ɵɵclassMap(i0.ɵɵpureFunction1(19, _c1, i0.ɵɵpipeBind1(17, 11, ctx.IsErrorHidden$)));
            i0.ɵɵadvance(2);
            i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(19, 13, ctx.Error$), " ");
            i0.ɵɵadvance(5);
            i0.ɵɵconditional(i0.ɵɵpipeBind1(24, 15, ctx.IsValidating$) ? 23 : -1);
        } }, dependencies: [AsyncPipe], styles: ["[_nghost-%COMP%]{background-color:#fff;border-radius:5px;width:65%;height:65%;display:flex;justify-content:center;align-items:center;z-index:1;text-align:center;font-family:Roboto,sans-serif}.main-container[_ngcontent-%COMP%]{display:flex;flex-direction:column;width:100%;height:100%}.title[_ngcontent-%COMP%]{display:flex;justify-content:center;align-items:center;font-size:28px;font-weight:700;padding-top:20px;padding-bottom:5px}.sub-title[_ngcontent-%COMP%]{display:flex;justify-content:center;align-items:center;font-size:14px;padding-top:5px;padding-bottom:20px}.original-text-container[_ngcontent-%COMP%]{display:flex;justify-content:center;align-items:center;width:100%;height:50%}.original-text-area[_ngcontent-%COMP%]{font-size:18px;height:90%;width:95%;resize:none;outline:none}.translated-text-container[_ngcontent-%COMP%]{display:flex;justify-content:center;align-items:center;width:100%;height:50%}.translated-text-area[_ngcontent-%COMP%]{font-size:18px;height:90%;width:95%;resize:none}.double-click[_ngcontent-%COMP%]{font-size:10px;font-style:italic}.button-container[_ngcontent-%COMP%]{display:flex;justify-content:space-between;margin-left:auto;margin-right:auto;padding-top:15px;padding-bottom:15px;width:96%}.save-button[_ngcontent-%COMP%]{border-width:0px;border-radius:5px;background-color:green;height:50px;width:125px;font-size:18px;font-weight:700;color:#fff;outline:none}.cancel-button[_ngcontent-%COMP%]{border-width:0px;border-radius:5px;background-color:red;height:50px;width:125px;font-size:18px;font-weight:700;color:#fff;outline:none}.error-message[_ngcontent-%COMP%]{display:block;color:red}.hidden[_ngcontent-%COMP%]{visibility:hidden}.spinner[_ngcontent-%COMP%]{border:7px solid #f3f3f3;border-top:7px solid #3498db;border-radius:50%;width:30px;height:30px;animation:_ngcontent-%COMP%_spin 2s linear infinite}@keyframes _ngcontent-%COMP%_spin{0%{transform:rotate(0)}to{transform:rotate(360deg)}}"] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TranslatorDialogComponent, [{
        type: Component,
        args: [{ selector: 'tc-translator-dialog', imports: [AsyncPipe], standalone: true, template: "<div class=\"main-container\">\n  <div class=\"title\">Translation Editor</div>\n  <div class=\"sub-title\">Translation Group: {{ data.GroupName }}</div>\n  <div class=\"original-text-container\">\n    <textarea readonly=\"readonly\" class=\"original-text-area\">{{\n      data.OriginalText\n    }}</textarea>\n  </div>\n  <div>\n    <span\n      class=\"double-click\"\n      [class]=\"{ hidden: TranslationExists$ | async }\"\n      >\n      (Double-Click Below to Copy From Above)\n    </span>\n  </div>\n  <div class=\"translated-text-container\">\n    <textarea\n      #translatedTextArea\n      class=\"translated-text-area\"\n      placeholder=\"Input translation here...\"\n      (dblclick)=\"copyOriginalText()\"\n      (change)=\"translatedTextChanged()\"\n      >{{ data.TranslatedText }}</textarea\n      >\n    </div>\n    <div class=\"error-message\" [class]=\"{ hidden: IsErrorHidden$ | async }\">\n      {{ Error$ | async }}\n    </div>\n    <div class=\"button-container\">\n      <button class=\"save-button\" (click)=\"save()\">Save</button>\n      @if (IsValidating$ | async) {\n        <div class=\"spinner\"></div>\n      }\n      <button class=\"cancel-button\" (click)=\"close()\">Cancel</button>\n    </div>\n  </div>\n", styles: [":host{background-color:#fff;border-radius:5px;width:65%;height:65%;display:flex;justify-content:center;align-items:center;z-index:1;text-align:center;font-family:Roboto,sans-serif}.main-container{display:flex;flex-direction:column;width:100%;height:100%}.title{display:flex;justify-content:center;align-items:center;font-size:28px;font-weight:700;padding-top:20px;padding-bottom:5px}.sub-title{display:flex;justify-content:center;align-items:center;font-size:14px;padding-top:5px;padding-bottom:20px}.original-text-container{display:flex;justify-content:center;align-items:center;width:100%;height:50%}.original-text-area{font-size:18px;height:90%;width:95%;resize:none;outline:none}.translated-text-container{display:flex;justify-content:center;align-items:center;width:100%;height:50%}.translated-text-area{font-size:18px;height:90%;width:95%;resize:none}.double-click{font-size:10px;font-style:italic}.button-container{display:flex;justify-content:space-between;margin-left:auto;margin-right:auto;padding-top:15px;padding-bottom:15px;width:96%}.save-button{border-width:0px;border-radius:5px;background-color:green;height:50px;width:125px;font-size:18px;font-weight:700;color:#fff;outline:none}.cancel-button{border-width:0px;border-radius:5px;background-color:red;height:50px;width:125px;font-size:18px;font-weight:700;color:#fff;outline:none}.error-message{display:block;color:red}.hidden{visibility:hidden}.spinner{border:7px solid #f3f3f3;border-top:7px solid #3498db;border-radius:50%;width:30px;height:30px;animation:spin 2s linear infinite}@keyframes spin{0%{transform:rotate(0)}to{transform:rotate(360deg)}}\n"] }]
    }], null, { translatedTextArea: [{
            type: ViewChild,
            args: ['translatedTextArea']
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(TranslatorDialogComponent, { className: "TranslatorDialogComponent", filePath: "lib/translator-dialog/translator-dialog.component.ts", lineNumber: 21 }); })();

class TranslationUpsert {
    constructor(applicationName, languageCode, group, keyTranslations) {
        this.applicationName = applicationName;
        this.languageCode = languageCode;
        this.group = group;
        this.keyTranslations = keyTranslations;
    }
}

class KeyTranslation {
    constructor(key, translatedText) {
        this.key = key;
        this.translatedText = translatedText;
    }
}

/**
 * Enum representing the different states of a translation within a system.
 *
 * The TranslationType enum defines the possible states a piece of content can be in
 * regarding its translation status. This can be useful for handling localization workflows,
 * content readiness, or translation management processes.
 */
var TranslationType;
(function (TranslationType) {
    TranslationType[TranslationType["ViewOnly"] = 0] = "ViewOnly";
    TranslationType[TranslationType["Unavailable"] = 1] = "Unavailable";
    TranslationType[TranslationType["Translated"] = 2] = "Translated";
    TranslationType[TranslationType["RequireTranslation"] = 3] = "RequireTranslation";
    TranslationType[TranslationType["RequireUpdate"] = 4] = "RequireUpdate";
})(TranslationType || (TranslationType = {}));

/**
 * An enumeration that defines the types of translation edits that can be applied.
 *
 * TranslationEditType provides various modes for handling translation content,
 * allowing customization of how text is displayed or utilized within a system.
 */
var TranslationEditType;
(function (TranslationEditType) {
    TranslationEditType[TranslationEditType["ViewOnly"] = 0] = "ViewOnly";
    TranslationEditType[TranslationEditType["UseHTML"] = 1] = "UseHTML";
    TranslationEditType[TranslationEditType["UseTranslation"] = 2] = "UseTranslation";
    TranslationEditType[TranslationEditType["UsePrimaryLanguageText"] = 3] = "UsePrimaryLanguageText";
})(TranslationEditType || (TranslationEditType = {}));

class Translation {
    constructor(key, text, userLanguageCode, translationLanguageCode, status, primaryLanguageText) {
        this.key = key;
        this.text = text;
        this.primaryLanguageText = primaryLanguageText;
        this.userLanguageCode = userLanguageCode;
        this.translationLanguageCode = translationLanguageCode;
        this.translationType = Translation.getTranslationType(status, this.userLanguageCode, this.translationLanguageCode);
        this.translationEditType = Translation.getTranslationEditType(this.translationType, this.userLanguageCode, this.translationLanguageCode);
    }
    static getTranslationType(status, userLanguageCode, translationLanguageCode) {
        if (status == null) {
            return TranslationType.ViewOnly;
        }
        if (translationLanguageCode !== userLanguageCode) {
            return TranslationType.RequireTranslation;
        }
        if (status === TranslationStatusType.RequireTranslation) {
            return TranslationType.RequireUpdate;
        }
        return TranslationType.Translated;
    }
    static getTranslationEditType(translationType, userLanguageCode, translationLanguageCode) {
        if (translationType == null) {
            return TranslationEditType.ViewOnly;
        }
        if (translationType === TranslationType.ViewOnly) {
            return TranslationEditType.ViewOnly;
        }
        if (userLanguageCode === LanguageType.EnglishUnitedStates) {
            return TranslationEditType.UseHTML;
        }
        if (translationLanguageCode === LanguageType.EnglishUnitedStates) {
            return TranslationEditType.UseTranslation;
        }
        return TranslationEditType.UsePrimaryLanguageText;
    }
    static CreateFromApiTranslation(translation, userLanguageCode) {
        return new Translation(translation.key, translation.text, userLanguageCode, translation.languageCode, translation.status, translation.primaryLanguageText);
    }
}

class MissingTranslation {
    constructor(applicationName, group, key, suggestedTranslation) {
        this.ApplicationName = applicationName;
        this.Group = group;
        this.Key = key;
        this.SuggestedTranslation = suggestedTranslation;
    }
}

class TranslationService {
    constructor() {
        this.http = inject(HttpClient);
        this.loggerService = inject(LoggerService);
        this.envService = inject(EnvironmentService);
        this.languageService = inject(LanguageService);
        this.modalService = inject(ModalService);
        this.groupName = inject(TranslationGroupNameToken);
        this.translationMap = new Map();
        this.missingTranslations = [];
        this.translationsUpdatedSubject = new Subject();
        /**
         * Observable that emits when translations have been reloaded for this translation group.
         * Useful for triggering UI updates or refreshing translation-dependent components.
         *
         * @example
         * ```typescript
         * this.translationService.translationsUpdated$.subscribe(() => {
         *   console.log('Translations reloaded!');
         *   this.refreshView();
         * });
         * ```
         */
        this.translationsUpdated$ = this.translationsUpdatedSubject.asObservable();
        this.keyUpdatedSubject = new Subject();
        /**
         * Observable that emits the key of a translation that was updated via the translation dialog.
         * Subscribe to this to react to individual translation changes.
         *
         * @example
         * ```typescript
         * this.translationService.keyUpdated$.subscribe((key) => {
         *   console.log(`Translation updated for key: ${key}`);
         *   this.refreshTranslation(key);
         * });
         * ```
         */
        this.keyUpdated$ = this.keyUpdatedSubject.asObservable();
        this.cacheId = '';
        this.languageChangedSubscription = this.languageService.languageChanged$
            .pipe(switchMap(() => this.loadTranslations(true)), tap((translationsLoaded) => {
            if (translationsLoaded) {
                this.translationsUpdatedSubject.next();
            }
        }))
            .subscribe();
    }
    ngOnDestroy() {
        if (this.languageChangedSubscription) {
            this.languageChangedSubscription.unsubscribe();
        }
    }
    loadTranslations(reload) {
        if (!reload && this.translationMap.size > 0) {
            return of(true);
        }
        this.translationMap.clear();
        return combineLatest([
            this.envService.configuration$,
            this.languageService.language$,
            this.languageService.isTranslating$,
        ]).pipe(first(), map(([configuration, language, isTranslating]) => {
            const apiUrl = configuration.translatorApiUrl ?? configuration.apiUrl;
            const endpoint = isTranslating
                ? configuration.getTranslatorTranslationsEndpoint
                : configuration.getTranslationsEndpoint;
            const url = `${apiUrl}/${endpoint}`;
            let params = new HttpParams();
            params = params.set('ApplicationName', configuration.applicationName);
            params = params.set('LanguageCode', language);
            params = params.set('Group', this.groupName);
            params = params.set('CacheId', this.cacheId);
            return { url, params, language };
        }), switchMap(({ url, params, language }) => this.http.get(url, { params }).pipe(map((translations) => {
            for (const translation of translations) {
                this.translationMap.set(translation.key, Translation.CreateFromApiTranslation(translation, language));
            }
            return true;
        }), retry(3), catchError((error) => {
            this.loggerService.log('TranslationService.loadTranslations', Severity.Error, this.createLogData(error));
            return of(false);
        }))));
    }
    loadTranslation(key) {
        return combineLatest([
            this.envService.configuration$,
            this.languageService.language$,
            this.languageService.isTranslating$,
        ]).pipe(first(), map(([configuration, language, isTranslating]) => {
            const apiUrl = configuration.translatorApiUrl ?? configuration.apiUrl;
            const endpoint = isTranslating
                ? configuration.getTranslatorTranslationEndpoint
                : configuration.getTranslationEndpoint;
            const url = `${apiUrl}/${endpoint}`;
            let params = new HttpParams();
            params = params.set('ApplicationName', configuration.applicationName);
            params = params.set('LanguageCode', language);
            params = params.set('Group', this.groupName);
            params = params.set('Key', key);
            params = params.set('CacheId', this.cacheId);
            return { url, params, language };
        }), switchMap(({ url, params, language }) => this.http.get(url, { params }).pipe(map((apiTranslation) => {
            const translation = Translation.CreateFromApiTranslation(apiTranslation, language);
            this.translationMap.set(translation.key, translation);
            return translation;
        }), retry(3), catchError((error) => {
            this.loggerService.log('TranslationService.loadTranslation', Severity.Error, this.createLogData(error));
            return of(null);
        }))));
    }
    /**
     * Fetches a translation from the API for a specific key.
     * If the translation doesn't exist or isn't translated, it will be tracked as missing.
     *
     * @param key - The translation key to fetch
     * @param suggestedTranslation - Default text to use if translation is missing
     * @returns Observable that emits the Translation object or null if not found
     *
     * @example
     * ```typescript
     * this.translationService.getTranslationFromApi(
     *   'app.welcome.title',
     *   'Welcome to our app'
     * ).subscribe((translation) => {
     *   this.title = translation?.text ?? 'Welcome to our app';
     * });
     * ```
     */
    getTranslationFromApi(key, suggestedTranslation) {
        return this.loadTranslation(key).pipe(tap((apiTranslation) => {
            if (this.isNotTranslated(key, apiTranslation)) {
                this.updateMissingTranslation(key, suggestedTranslation);
                this.missingTranslations.push(key);
            }
        }));
    }
    /**
     * Gets a translation from the local cache by key.
     * This is a synchronous method that returns immediately.
     * Returns null if the translation hasn't been loaded yet.
     *
     * @param key - The translation key to retrieve
     * @returns The Translation object if cached, null otherwise
     *
     * @example
     * ```typescript
     * const translation = this.translationService.getTranslation('app.title');
     * if (translation) {
     *   this.pageTitle = translation.text;
     * }
     * ```
     */
    getTranslation(key) {
        const translation = this.translationMap.get(key);
        return translation ?? null;
    }
    /**
     * Loads multiple specific translations by their keys in a single batch operation.
     * More efficient than loading translations individually when you need multiple keys.
     *
     * @param keys - Array of translation keys to load
     * @returns Observable that emits an array of Translation objects (null for keys not found)
     *
     * @example
     * ```typescript
     * const keys = ['app.title', 'app.welcome', 'app.footer.copyright'];
     * this.translationService.batchLoadTranslations(keys).subscribe((translations) => {
     *   translations.forEach((translation, index) => {
     *     if (translation) {
     *       console.log(`${keys[index]}: ${translation.text}`);
     *     }
     *   });
     * });
     * ```
     *
     * @example
     * ```typescript
     * // Load translations for a form
     * const formKeys = [
     *   'form.firstName.label',
     *   'form.lastName.label',
     *   'form.email.label',
     *   'form.submit.button'
     * ];
     *
     * this.translationService.batchLoadTranslations(formKeys).subscribe((translations) => {
     *   this.formLabels = Object.fromEntries(
     *     formKeys.map((key, i) => [key, translations[i]?.text ?? key])
     *   );
     * });
     * ```
     */
    batchLoadTranslations(keys) {
        // Check cache first
        const cachedTranslations = keys.map((key) => this.translationMap.get(key) ?? null);
        // If all translations are cached, return immediately
        if (cachedTranslations.every((t) => t !== null)) {
            return of(cachedTranslations);
        }
        // Otherwise, load all translations and return the requested keys
        return this.loadTranslations().pipe(map((success) => {
            if (!success) {
                return keys.map(() => null);
            }
            return keys.map((key) => this.translationMap.get(key) ?? null);
        }));
    }
    /**
     * Observes a specific translation key and emits whenever that translation is updated.
     * Useful for reactive UI components that need to respond to translation changes.
     *
     * @param key - The translation key to observe
     * @returns Observable that emits the Translation object whenever it's updated
     *
     * @example
     * ```typescript
     * // In a component
     * readonly welcomeText$ = this.translationService.select('app.welcome');
     *
     * // In template
     * <h1>{{ (welcomeText$ | async)?.text ?? 'Welcome' }}</h1>
     * ```
     *
     * @example
     * ```typescript
     * // React to translation updates
     * this.translationService.select('app.notification.message').subscribe((translation) => {
     *   if (translation) {
     *     this.showNotification(translation.text);
     *   }
     * });
     * ```
     *
     * @example
     * ```typescript
     * // Combine multiple translations
     * combineLatest([
     *   this.translationService.select('form.firstName'),
     *   this.translationService.select('form.lastName')
     * ]).subscribe(([firstName, lastName]) => {
     *   this.formLabels = {
     *     firstName: firstName?.text ?? 'First Name',
     *     lastName: lastName?.text ?? 'Last Name'
     *   };
     * });
     * ```
     */
    select(key) {
        return new Observable((observer) => {
            // Emit current value immediately
            const currentTranslation = this.translationMap.get(key) ?? null;
            observer.next(currentTranslation);
            // Subscribe to updates for this specific key
            const keySubscription = this.keyUpdated$
                .pipe(map((updatedKey) => updatedKey === key
                ? this.translationMap.get(key) ?? null
                : null))
                .subscribe((translation) => {
                if (translation !== null) {
                    observer.next(translation);
                }
            });
            // Subscribe to bulk translation updates
            const bulkSubscription = this.translationsUpdated$
                .pipe(map(() => this.translationMap.get(key) ?? null))
                .subscribe((translation) => {
                observer.next(translation);
            });
            // Cleanup subscriptions
            return () => {
                keySubscription.unsubscribe();
                bulkSubscription.unsubscribe();
            };
        });
    }
    isNotTranslated(key, translation) {
        return ((translation == null ||
            translation.userLanguageCode !==
                translation.translationLanguageCode) &&
            !this.missingTranslations.includes(key));
    }
    /**
     * Updates or creates a translation in the local cache.
     * This method is typically called after a successful translation edit via the dialog.
     * Emits on keyUpdated$ when complete.
     *
     * @param key - The translation key
     * @param originalText - The primary language text
     * @param translatedText - The translated text
     *
     * @example
     * ```typescript
     * this.translationService.updateTranslationMap(
     *   'app.welcome',
     *   'Welcome',
     *   'Bienvenue'
     * );
     *
     * // Listen for the update
     * this.translationService.keyUpdated$.subscribe((key) => {
     *   if (key === 'app.welcome') {
     *     this.refreshWelcomeText();
     *   }
     * });
     * ```
     */
    updateTranslationMap(key, originalText, translatedText) {
        let translation = this.translationMap.get(key);
        this.languageService.language$
            .pipe(first())
            .subscribe((languageCode) => {
            if (translation == null) {
                translation = new Translation(key, translatedText, languageCode, languageCode, TranslationStatusType.Translated, originalText);
                this.translationMap.set(key, translation);
            }
            else {
                translation.text = translatedText;
                translation.primaryLanguageText = originalText;
                translation.translationLanguageCode = languageCode;
                translation.translationType = TranslationType.Translated;
                translation.translationEditType =
                    TranslationEditType.UsePrimaryLanguageText;
            }
            this.keyUpdatedSubject.next(key);
        });
    }
    /**
     * Opens the translation editor dialog for a specific translation key.
     * Used by translation directives when clicking on translatable elements in translation mode.
     *
     * @param key - The translation key to edit
     * @param originalText - The primary language text
     * @param translatedText - The current translated text
     * @param whenClosed - Callback invoked when the dialog closes with optional result
     *
     * @example
     * ```typescript
     * this.translationService.showDialog(
     *   'app.welcome',
     *   'Welcome',
     *   'Bienvenue',
     *   (result) => {
     *     if (result?.success) {
     *       console.log('Translation saved successfully');
     *     }
     *   }
     * );
     * ```
     */
    showDialog(key, originalText, translatedText, whenClosed) {
        combineLatest([
            this.envService.configuration$,
            this.languageService.language$,
        ])
            .pipe(first())
            .subscribe(([configuration, language]) => {
            const apiUrl = configuration.translatorApiUrl ?? configuration.apiUrl;
            const url = `${apiUrl}/${configuration.upsertTranslationEndpoint}`;
            this.modalService.showModal(TranslatorDialogComponent, {
                closeOnOverlayClick: false,
                data: {
                    ApplicationName: configuration.applicationName,
                    Url: url,
                    Language: language,
                    GroupName: this.groupName,
                    Key: key,
                    OriginalText: originalText,
                    TranslatedText: translatedText,
                },
                validateBeforeClosing: (data) => this.upsert(this.http, data),
                whenClosed,
            });
        });
    }
    upsert(http, data) {
        const body = new TranslationUpsert(data.ApplicationName, data.Language, data.GroupName, [new KeyTranslation(data.Key, data.TranslatedText)]);
        return http.post(data.Url, body).pipe(map((response) => new TranslationDialogResult(data, response)), retry(3), catchError((error) => {
            this.loggerService.log('TranslationService.upsert', Severity.Error, this.createLogData(error));
            return of(new TranslationDialogResult(data, error));
        }));
    }
    updateMissingTranslation(key, suggestedTranslation) {
        this.envService.configuration$
            .pipe(first(), switchMap((configuration) => {
            const apiUrl = configuration.translatorApiUrl ??
                configuration.apiUrl;
            const url = `${apiUrl}/${configuration.updateMissingTranslationEndpoint}`;
            const body = new MissingTranslation(configuration.applicationName, this.groupName, key, suggestedTranslation);
            return this.http.post(url, body).pipe(retry(3), catchError((error) => {
                this.loggerService.log('TranslationService.updateMissingTranslation', Severity.Error, this.createLogData(error));
                return EMPTY;
            }));
        }))
            .subscribe();
    }
    createLogData(error) {
        return {
            message: error.message,
            name: error.name,
            status: error.status,
            statusText: error.statusText,
            url: error.url ?? undefined,
            ok: error.ok,
            error: error.error,
        };
    }
    static { this.ɵfac = function TranslationService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || TranslationService)(); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: TranslationService, factory: TranslationService.ɵfac }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TranslationService, [{
        type: Injectable
    }], () => [], null); })();

class BaseDirective {
    get ElementRef() {
        return this.elementRef;
    }
    get OriginalText() {
        return this.originalText.trim();
    }
    set OriginalText(text) {
        this.originalText = text ?? '';
    }
    get IsShowingIndicator() {
        return this.isShowingIndicator;
    }
    set IsShowingIndicator(isShowing) {
        this.isShowingIndicator = isShowing;
    }
    constructor() {
        this.elementRef = inject(ElementRef);
        this.renderer = inject(Renderer2);
        this.translationService = inject(TranslationService);
        this.languageService = inject(LanguageService);
        this.translationsUpdatedSubscription = null;
        this.keyUpdatedSubscription = null;
        this.isTranslatingSubscription = null;
        this.isTranslating = false;
        this.elementPropertyName = '';
        this.groupName = '';
        this.translationIndicatorClassName = null;
        this.originalText = '';
        this.isShowingIndicator = false;
        this.translationsUpdatedSubscription =
            this.translationService.translationsUpdated$.subscribe(() => this.translate());
        this.keyUpdatedSubscription =
            this.translationService.keyUpdated$.subscribe((key) => {
                if (this.key === key) {
                    this.translate();
                }
            });
    }
    ngOnInit() {
        this.translate();
        this.isTranslatingSubscription =
            this.languageService.isTranslating$.subscribe((isTranslating) => {
                if (this.isTranslating !== isTranslating) {
                    this.isTranslating = isTranslating;
                    this.translate();
                }
                if (this.forceForward && this.isTranslating) {
                    this.renderer.setStyle(this.ElementRef.nativeElement, 'pointer-events', 'auto', RendererStyleFlags2.Important);
                }
                else if (this.forceForward) {
                    this.renderer.removeStyle(this.ElementRef.nativeElement, 'pointer-events');
                }
            });
    }
    ngOnDestroy() {
        const subscriptions = [
            this.translationsUpdatedSubscription,
            this.keyUpdatedSubscription,
            this.isTranslatingSubscription,
        ];
        subscriptions.forEach((subscription) => subscription?.unsubscribe());
    }
    getOriginalText(translation) {
        if (translation == null ||
            translation.translationEditType === TranslationEditType.UseHTML) {
            return this.OriginalText;
        }
        if (translation.translationEditType ===
            TranslationEditType.UseTranslation) {
            return translation.text;
        }
        return translation.primaryLanguageText ?? this.OriginalText;
    }
    getTranslatedText(translation) {
        if (translation == null ||
            translation.userLanguageCode !== translation.translationLanguageCode) {
            return '';
        }
        return translation.text;
    }
    translate() {
        const translation = this.translationService.getTranslation(this.key);
        if (translation == null) {
            this.renderer.setProperty(this.elementRef.nativeElement, this.elementPropertyName, this.OriginalText);
        }
        else {
            this.renderer.setProperty(this.elementRef.nativeElement, this.elementPropertyName, translation.text);
        }
        this.applyIndicator(translation);
    }
    updateTranslationMap(key, originalText, translatedText) {
        this.translationService.updateTranslationMap(key, originalText, translatedText);
    }
    openDialog() {
        this.languageService.language$.pipe(first()).subscribe((language) => {
            const translation = this.translationService.getTranslation(this.key);
            if (language !== LanguageType.EnglishUnitedStates &&
                translation == null) {
                BaseDirective.showAlert();
                return;
            }
            const originalText = this.getOriginalText(translation);
            const translatedText = this.getTranslatedText(translation);
            this.translationService.showDialog(this.key, originalText, translatedText, (result) => BaseDirective.HandleDialogResult(this, result));
        });
    }
    static showAlert() {
        alert('An initial value for the English translation is required in order to apply alternate language translations.');
    }
    applyIndicator(translation) {
        this.languageService.language$.pipe(first()).subscribe((language) => {
            if (!this.isTranslating) {
                BaseDirective.RemoveTranslationIndicator(this);
                return;
            }
            if (translation == null) {
                if (language === LanguageType.EnglishUnitedStates) {
                    BaseDirective.ShowTranslationIndicator(this, this.validTranslationIndicatorStyles);
                }
                else {
                    BaseDirective.ShowTranslationIndicator(this, this.invalidTranslationIndicatorStyles);
                }
            }
            else if (translation.translationType ===
                TranslationType.RequireTranslation ||
                translation.translationType === TranslationType.RequireUpdate) {
                BaseDirective.ShowTranslationIndicator(this, this.validTranslationIndicatorStyles);
            }
            else {
                BaseDirective.RemoveTranslationIndicator(this);
            }
        });
    }
    /**
     * This method is executed outside the scope of this instance, when called by the dialog, so
     * it was prudent to make it static, and it needs a reference to an instance of this directive.
     *
     * @param directive An instance of this directive
     * @param result The result passed back by the dialog
     */
    static HandleDialogResult(directive, result) {
        if (!result?.isSuccess) {
            return;
        }
        directive.renderer.setProperty(directive.ElementRef.nativeElement, directive.elementPropertyName, result.data.TranslatedText);
        directive.updateTranslationMap(result.data.Key, result.data.OriginalText, result.data.TranslatedText);
        BaseDirective.RemoveTranslationIndicator(directive);
    }
    /**
     * This method is executed outside the scope of this instance, when called by the dialog, so
     * it was prudent to make it static, and it needs a reference to an instance of this directive.
     *
     * @param directive An instance of this directive
     */
    static ShowTranslationIndicator(directive, styles) {
        if (directive.translationIndicatorClassName != null) {
            directive.renderer.addClass(directive.ElementRef.nativeElement, directive.translationIndicatorClassName);
        }
        else {
            styles.forEach((style) => {
                directive.renderer.setStyle(directive.ElementRef.nativeElement, style[0], style[1]);
            });
            if (directive.forceForward) {
                directive.renderer.setStyle(directive.ElementRef.nativeElement, 'z-index', 9999);
                directive.renderer.setStyle(directive.ElementRef.nativeElement, 'position', 'absolute');
            }
        }
        directive.IsShowingIndicator = true;
    }
    /**
     * This method is executed outside the scope of this instance, when called by the dialog, so
     * it was prudent to make it static, and it needs a reference to an instance of this directive.
     *
     * @param directive An instance of this directive
     */
    static RemoveTranslationIndicator(directive) {
        if (!directive.IsShowingIndicator) {
            return;
        }
        if (directive.translationIndicatorClassName != null) {
            directive.ElementRef.nativeElement.classList.remove(directive.translationIndicatorClassName);
        }
        else {
            directive.validTranslationIndicatorStyles.forEach((style) => {
                directive.renderer.removeStyle(directive.ElementRef.nativeElement, style[0]);
            });
            if (directive.forceForward) {
                directive.renderer.removeStyle(directive.ElementRef.nativeElement, 'z-index');
                directive.renderer.removeStyle(directive.ElementRef.nativeElement, 'position');
            }
        }
        directive.IsShowingIndicator = false;
    }
    static { this.ɵfac = function BaseDirective_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || BaseDirective)(); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: BaseDirective }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(BaseDirective, [{
        type: Directive
    }], () => [], null); })();

class TranslationDirective extends BaseDirective {
    onClick(event) {
        if (this.isTranslating) {
            event.preventDefault();
            event.stopPropagation();
            event.cancelBubble = true;
            if (event.ctrlKey || event.altKey || event.metaKey) {
                this.followPotentialLink(event);
                return;
            }
            this.openDialog();
        }
    }
    constructor() {
        const groupName = inject(TranslationGroupNameToken);
        const translationIndicatorClassName = inject(TranslationIndicatorClassToken);
        super();
        this.router = inject(Router);
        this.forceForward = false;
        this.validTranslationIndicatorStyles = [
            ['cursor', 'pointer'],
            ['border-style', 'dotted'],
            ['border-color', 'red'],
            ['border-width', '1px'],
            ['border-radius', '5px'],
        ];
        this.invalidTranslationIndicatorStyles = [
            ['cursor', 'pointer'],
            ['border-style', 'dotted'],
            ['border-color', 'grey'],
            ['border-width', '1px'],
            ['border-radius', '5px'],
        ];
        this.elementPropertyName = 'innerHTML';
        this.groupName = groupName;
        this.translationIndicatorClassName = translationIndicatorClassName;
    }
    ngOnInit() {
        this.OriginalText = this.ElementRef.nativeElement.innerHTML;
        super.ngOnInit();
    }
    followPotentialLink(event) {
        const nativeElement = this.ElementRef.nativeElement;
        const href = nativeElement.getAttribute('href') ??
            nativeElement.parentElement?.getAttribute('href');
        if (href != null) {
            this.router.navigateByUrl(href);
            return;
        }
        const target = event.target;
        if (!(target instanceof HTMLElement)) {
            return;
        }
        target.parentElement?.click?.();
    }
    static { this.ɵfac = function TranslationDirective_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || TranslationDirective)(); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: TranslationDirective, selectors: [["", "translation", ""]], hostBindings: function TranslationDirective_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("click", function TranslationDirective_click_HostBindingHandler($event) { return ctx.onClick($event); });
        } }, inputs: { key: [0, "translation", "key"], forceForward: [0, "translationForceForward", "forceForward"] }, features: [i0.ɵɵInheritDefinitionFeature] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TranslationDirective, [{
        type: Directive,
        args: [{
                // eslint-disable-next-line @angular-eslint/directive-selector
                selector: '[translation]',
                standalone: true,
            }]
    }], () => [], { key: [{
            type: Input,
            args: ['translation']
        }], forceForward: [{
            type: Input,
            args: ['translationForceForward']
        }], onClick: [{
            type: HostListener,
            args: ['click', ['$event']]
        }] }); })();

class PlaceHolderTranslationDirective extends BaseDirective {
    onClick(event) {
        if (this.isTranslating) {
            event.preventDefault();
            event.stopPropagation();
            event.cancelBubble = true;
            if (event.ctrlKey || event.altKey || event.metaKey) {
                return;
            }
            this.openDialog();
        }
    }
    constructor() {
        const groupName = inject(TranslationGroupNameToken);
        super();
        // eslint-disable-next-line @angular-eslint/no-input-rename
        this.forceForward = false;
        this.validTranslationIndicatorStyles = [
            ['background-color', '#FFB6C1'],
        ];
        this.invalidTranslationIndicatorStyles = [
            ['background-color', 'grey'],
        ];
        this.elementPropertyName = 'placeholder';
        this.groupName = groupName;
    }
    ngOnInit() {
        const placeholder = this.elementRef.nativeElement.getAttribute('placeholder');
        this.OriginalText = placeholder ?? '';
        super.ngOnInit();
    }
    static { this.ɵfac = function PlaceHolderTranslationDirective_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || PlaceHolderTranslationDirective)(); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: PlaceHolderTranslationDirective, selectors: [["", "placeholderTranslation", ""]], hostBindings: function PlaceHolderTranslationDirective_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("click", function PlaceHolderTranslationDirective_click_HostBindingHandler($event) { return ctx.onClick($event); });
        } }, inputs: { key: [0, "placeholderTranslation", "key"], forceForward: [0, "translationForceForward", "forceForward"] }, features: [i0.ɵɵInheritDefinitionFeature] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PlaceHolderTranslationDirective, [{
        type: Directive,
        args: [{
                standalone: true,
                // eslint-disable-next-line @angular-eslint/directive-selector
                selector: '[placeholderTranslation]',
            }]
    }], () => [], { key: [{
            type: Input,
            args: ['placeholderTranslation']
        }], forceForward: [{
            type: Input,
            args: ['translationForceForward']
        }], onClick: [{
            type: HostListener,
            args: ['click', ['$event']]
        }] }); })();

/**
 * @deprecated Remove from project
 */
function MockPlaceHolderTranslationDirective() {
    const metadata = {
        selector: '[placeholderTranslation]',
        inputs: ['placeholderTranslation', 'translationForceForward'],
    };
    return Directive(metadata)(class MockPlaceHolderTranslationDirectiveClass {
    });
}

class AriaLabelTranslationDirective extends BaseDirective {
    onClick(event) {
        if (this.isTranslating) {
            event.preventDefault();
            event.stopPropagation();
            event.cancelBubble = true;
            if (event.ctrlKey || event.altKey || event.metaKey) {
                return;
            }
            this.openDialog();
        }
    }
    constructor() {
        const groupName = inject(TranslationGroupNameToken);
        super();
        // eslint-disable-next-line @angular-eslint/no-input-rename
        this.forceForward = false;
        this.validTranslationIndicatorStyles = [
            ['cursor', 'pointer'],
            ['border-style', 'dotted'],
            ['border-color', 'red'],
            ['border-width', '1px'],
            ['border-radius', '5px'],
        ];
        this.invalidTranslationIndicatorStyles = [
            ['cursor', 'pointer'],
            ['border-style', 'dotted'],
            ['border-color', 'grey'],
            ['border-width', '1px'],
            ['border-radius', '5px'],
        ];
        this.elementPropertyName = 'aria-label';
        this.groupName = groupName;
    }
    ngOnInit() {
        const ariaLabel = this.elementRef.nativeElement.getAttribute('aria-label');
        this.OriginalText = ariaLabel ?? '';
        super.ngOnInit();
    }
    static { this.ɵfac = function AriaLabelTranslationDirective_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || AriaLabelTranslationDirective)(); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: AriaLabelTranslationDirective, selectors: [["", "ariaLabelTranslation", ""]], hostBindings: function AriaLabelTranslationDirective_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("click", function AriaLabelTranslationDirective_click_HostBindingHandler($event) { return ctx.onClick($event); });
        } }, inputs: { key: [0, "ariaLabelTranslation", "key"], forceForward: [0, "translationForceForward", "forceForward"] }, features: [i0.ɵɵInheritDefinitionFeature] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AriaLabelTranslationDirective, [{
        type: Directive,
        args: [{
                standalone: true,
                // eslint-disable-next-line @angular-eslint/directive-selector
                selector: '[ariaLabelTranslation]',
            }]
    }], () => [], { key: [{
            type: Input,
            args: ['ariaLabelTranslation']
        }], forceForward: [{
            type: Input,
            args: ['translationForceForward']
        }], onClick: [{
            type: HostListener,
            args: ['click', ['$event']]
        }] }); })();

/**
 * @deprecated Remove from project
 */
function MockAriaLabelTranslationDirective() {
    const metadata = {
        selector: '[ariaLabelTranslation]',
        inputs: ['ariaLabelTranslation', 'translationForceForward'],
    };
    return Directive(metadata)(class MockAriaLabelTranslationDirectiveClass {
    });
}

/**
 * TranslationResolver is a resolver class that implements the Resolve interface
 * to fetch necessary translations before a route is activated.
 *
 * It uses the TranslationService to load translations and ensures that the
 * application has the required translations before navigating to the target route.
 *
 * This class is typically used with Angular's route resolvers to prefetch data
 * necessary for the application, improving the user experience by ensuring
 * translations are available before the route is rendered.
 */
class TranslationResolver {
    constructor() {
        this.translationService = inject(TranslationService);
    }
    /**
     * Resolves and loads the required translations using the translation service.
     *
     * @return {Observable<boolean>} An observable that emits a boolean indicating the success of loading translations.
     */
    resolve() {
        return this.translationService.loadTranslations();
    }
    static { this.ɵfac = function TranslationResolver_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || TranslationResolver)(); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: TranslationResolver, factory: TranslationResolver.ɵfac }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TranslationResolver, [{
        type: Injectable
    }], null, null); })();

/**
 * @deprecated Remove from project
 */
class MockLanguageService {
    constructor(canTranslate = false, isTranslating = false) {
        this.availableLanguagesSubject = new ReplaySubject(1);
        this.availableLanguages$ = this.availableLanguagesSubject.asObservable();
        this.languageSubject = new ReplaySubject(1);
        this.language$ = this.languageSubject.asObservable();
        this.languageChangedSubject = new ReplaySubject(1);
        this.languageChanged$ = this.languageChangedSubject.asObservable();
        this.isTranslatingSubject = new BehaviorSubject(false);
        this.isTranslating$ = this.isTranslatingSubject.asObservable();
        this.canTranslateSubject = new BehaviorSubject(false);
        this.canTranslate$ = this.canTranslateSubject.asObservable();
        this.isTranslating = false;
        this.canTranslateSubject.next(canTranslate);
        this.isTranslatingSubject.next(isTranslating);
    }
    setIsTranslating(isTranslating) {
        this.isTranslatingSubject.next(isTranslating);
        this.initLanguage().subscribe();
    }
    get IsTranslating() {
        return this.isTranslating;
    }
    set IsTranslating(isTranslating) {
        if (this.canTranslateSubject.value) {
            this.isTranslating = isTranslating;
            this.initLanguage().subscribe(() => this.isTranslatingSubject.next(isTranslating));
        }
    }
    initLanguage() {
        return of(undefined);
    }
    changeLanguage(language) {
        this.languageSubject.next(language);
        this.languageChangedSubject.next(language);
    }
}

/**
 * Mock implementation of TranslationService for testing.
 * Matches the real TranslationService API 1:1.
 *
 * @example
 * ```typescript
 * // In tests
 * const mockTranslations = [
 *   new Translation('app.title', 'My App', 'en-us', 'en-us', TranslationStatusType.Translated)
 * ];
 * const mockService = new MockTranslationService(mockTranslations);
 *
 * TestBed.configureTestingModule({
 *   providers: [
 *     { provide: TranslationService, useValue: mockService }
 *   ]
 * });
 * ```
 */
class MockTranslationService {
    constructor(translations) {
        this.translationMap = new Map();
        this.translationsUpdatedSubject = new Subject();
        this.translationsUpdated$ = this.translationsUpdatedSubject.asObservable();
        this.keyUpdatedSubject = new Subject();
        this.keyUpdated$ = this.keyUpdatedSubject.asObservable();
        if (translations) {
            translations.forEach((t) => {
                this.translationMap.set(t.key, t);
            });
        }
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    loadTranslations(_reload) {
        return of(true);
    }
    getTranslationFromApi(key, 
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _suggestedTranslation) {
        return of(this.translationMap.get(key) ?? null);
    }
    getTranslation(key) {
        return this.translationMap.get(key) ?? null;
    }
    translationExists(key) {
        const translation = this.translationMap.get(key);
        return (translation != null &&
            translation.userLanguageCode === translation.translationLanguageCode);
    }
    updateTranslationMap(key, originalText, translatedText) {
        let translation = this.translationMap.get(key);
        if (translation == null) {
            translation = new Translation(key, translatedText, 'en-us', 'en-us', TranslationStatusType.Translated, originalText);
            this.translationMap.set(key, translation);
        }
        else {
            translation.text = translatedText;
            translation.primaryLanguageText = originalText;
            translation.translationLanguageCode = 'en-us';
            translation.translationType = TranslationType.Translated;
            translation.translationEditType =
                TranslationEditType.UsePrimaryLanguageText;
        }
        this.keyUpdatedSubject.next(key);
    }
    showDialog(key, originalText, translatedText, whenClosed) {
        void key;
        void originalText;
        void translatedText;
        whenClosed();
    }
    /**
     * Loads multiple translations by their keys in a single batch operation.
     * In the mock, returns cached translations immediately.
     *
     * @param keys - Array of translation keys to load
     * @returns Observable that emits an array of Translation objects
     */
    batchLoadTranslations(keys) {
        return of(keys.map((key) => this.translationMap.get(key) ?? null));
    }
    /**
     * Observes a specific translation key and emits whenever it's updated.
     * In the mock, emits the current value immediately and subscribes to keyUpdated$.
     *
     * @param key - The translation key to observe
     * @returns Observable that emits the Translation object
     */
    select(key) {
        return new Observable((observer) => {
            // Emit current value
            observer.next(this.translationMap.get(key) ?? null);
            // Subscribe to updates
            const subscription = this.keyUpdated$
                .pipe(map((updatedKey) => updatedKey === key
                ? this.translationMap.get(key) ?? null
                : null))
                .subscribe((translation) => {
                if (translation !== null) {
                    observer.next(translation);
                }
            });
            return () => subscription.unsubscribe();
        });
    }
    // === Test Helper Methods ===
    /**
     * Simulates a translation update for testing reactive behavior.
     *
     * @param key - The translation key that was updated
     *
     * @example
     * ```typescript
     * mockService.select('app.title').subscribe((translation) => {
     *   console.log('Title updated:', translation?.text);
     * });
     *
     * mockService.simulateTranslationUpdate('app.title');
     * ```
     */
    simulateTranslationUpdate(key) {
        this.keyUpdatedSubject.next(key);
    }
    /**
     * Simulates a bulk translation reload for testing.
     *
     * @example
     * ```typescript
     * mockService.translationsUpdated$.subscribe(() => {
     *   console.log('All translations reloaded');
     * });
     *
     * mockService.simulateTranslationsReload();
     * ```
     */
    simulateTranslationsReload() {
        this.translationsUpdatedSubject.next();
    }
}

class TranslatorViewService {
    constructor() {
        this.languageService = inject(LanguageService);
        this.isTranslating = false;
        this.showAllMessagesSubject = new BehaviorSubject(false);
        this.showAllMessages$ = this.showAllMessagesSubject.asObservable();
        this.translateSubscription =
            this.languageService.isTranslating$.subscribe((isTranslating) => {
                this.isTranslating = isTranslating;
                if (!this.isTranslating && this.showAllMessagesSubject.value) {
                    this.showAllMessagesSubject.next(false);
                }
            });
    }
    ngOnDestroy() {
        if (this.translateSubscription != null) {
            this.translateSubscription.unsubscribe();
        }
    }
    showAllMessages(show) {
        if (show && this.isTranslating) {
            this.showAllMessagesSubject.next(true);
        }
        else {
            this.showAllMessagesSubject.next(false);
        }
    }
    static { this.ɵfac = function TranslatorViewService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || TranslatorViewService)(); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: TranslatorViewService, factory: TranslatorViewService.ɵfac, providedIn: 'root' }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TranslatorViewService, [{
        type: Injectable,
        args: [{ providedIn: 'root' }]
    }], () => [], null); })();

/**
 * @deprecated Remove from project
 */
class MockTranslatorViewService {
    constructor(canShowAllMessages = false) {
        this.canShowAllMessages = false;
        this.showAllMessagesSubject = new BehaviorSubject(false);
        this.showAllMessages$ = this.showAllMessagesSubject.asObservable();
        this.canShowAllMessages = canShowAllMessages;
    }
    showAllMessages(show) {
        if (show && this.canShowAllMessages) {
            this.showAllMessagesSubject.next(true);
        }
        else {
            this.showAllMessagesSubject.next(false);
        }
    }
}

const _c0 = a0 => ({ depressed: a0 });
function TranslationModeComponent_Conditional_0_Conditional_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span");
    i0.ɵɵtext(1, "Translate");
    i0.ɵɵelementEnd();
} }
function TranslationModeComponent_Conditional_0_Conditional_6_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span");
    i0.ɵɵtext(1, "Translate All");
    i0.ɵɵelementEnd();
} }
function TranslationModeComponent_Conditional_0_Conditional_9_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span");
    i0.ɵɵtext(1, "Normal Mode");
    i0.ɵɵelementEnd();
} }
function TranslationModeComponent_Conditional_0_Template(rf, ctx) { if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "span")(1, "button", 0);
    i0.ɵɵpipe(2, "async");
    i0.ɵɵlistener("click", function TranslationModeComponent_Conditional_0_Template_button_click_1_listener() { i0.ɵɵrestoreView(_r1); const ctx_r1 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r1.setIsTranslating()); });
    i0.ɵɵconditionalCreate(3, TranslationModeComponent_Conditional_0_Conditional_3_Template, 2, 0, "span");
    i0.ɵɵpipe(4, "async");
    i0.ɵɵpipe(5, "async");
    i0.ɵɵconditionalCreate(6, TranslationModeComponent_Conditional_0_Conditional_6_Template, 2, 0, "span");
    i0.ɵɵpipe(7, "async");
    i0.ɵɵpipe(8, "async");
    i0.ɵɵconditionalCreate(9, TranslationModeComponent_Conditional_0_Conditional_9_Template, 2, 0, "span");
    i0.ɵɵpipe(10, "async");
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction1(16, _c0, i0.ɵɵpipeBind1(2, 4, ctx_r1.isTranslating$)));
    i0.ɵɵadvance(2);
    i0.ɵɵconditional(i0.ɵɵpipeBind1(4, 6, ctx_r1.isTranslating$) === true && i0.ɵɵpipeBind1(5, 8, ctx_r1.showAllMessages$) === false ? 3 : -1);
    i0.ɵɵadvance(3);
    i0.ɵɵconditional(i0.ɵɵpipeBind1(7, 10, ctx_r1.isTranslating$) === true && i0.ɵɵpipeBind1(8, 12, ctx_r1.showAllMessages$) === true ? 6 : -1);
    i0.ɵɵadvance(3);
    i0.ɵɵconditional(i0.ɵɵpipeBind1(10, 14, ctx_r1.isTranslating$) === false ? 9 : -1);
} }
class TranslationModeComponent {
    constructor() {
        this.translatorViewService = inject(TranslatorViewService);
        this.languageService = inject(LanguageService);
        this.canTranslate$ = this.languageService.canTranslate$;
        this.isTranslating$ = this.languageService.isTranslating$;
        this.showAllMessages$ = this.translatorViewService.showAllMessages$;
    }
    setIsTranslating() {
        combineLatest([this.isTranslating$, this.showAllMessages$])
            .pipe(first())
            .subscribe(([isTranslating, showingAllMessages]) => {
            if (!isTranslating) {
                this.languageService.setIsTranslating(true);
            }
            else if (isTranslating && !showingAllMessages) {
                this.translatorViewService.showAllMessages(true);
            }
            else {
                this.languageService.setIsTranslating(false);
                this.translatorViewService.showAllMessages(false);
            }
        });
    }
    static { this.ɵfac = function TranslationModeComponent_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || TranslationModeComponent)(); }; }
    static { this.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: TranslationModeComponent, selectors: [["tc-translation-mode"]], decls: 2, vars: 3, consts: [[1, "translator-mode-button", 3, "click", "ngClass"]], template: function TranslationModeComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵconditionalCreate(0, TranslationModeComponent_Conditional_0_Template, 11, 18, "span");
            i0.ɵɵpipe(1, "async");
        } if (rf & 2) {
            i0.ɵɵconditional(i0.ɵɵpipeBind1(1, 1, ctx.canTranslate$) === true ? 0 : -1);
        } }, dependencies: [NgClass, AsyncPipe], styles: [".translator-mode-button[_ngcontent-%COMP%]{background-color:red;border-radius:5px;border-style:outset;border-width:1px;color:#fff;font-weight:700;font-size:12px;width:100px;height:35px;padding:0}.depressed[_ngcontent-%COMP%]{border-style:inset}"] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TranslationModeComponent, [{
        type: Component,
        args: [{ selector: 'tc-translation-mode', standalone: true, imports: [AsyncPipe, NgClass], template: "@if ((canTranslate$ | async) === true) {\n  <span>\n    <button\n      class=\"translator-mode-button\"\n      [ngClass]=\"{ depressed: (isTranslating$ | async) }\"\n      (click)=\"setIsTranslating()\"\n      >\n      @if (\n        (isTranslating$ | async) === true &&\n        (showAllMessages$ | async) === false\n        ) {\n        <span\n          >Translate</span\n          >\n        }\n        @if (\n          (isTranslating$ | async) === true &&\n          (showAllMessages$ | async) === true\n          ) {\n          <span\n            >Translate All</span\n            >\n          }\n          @if ((isTranslating$ | async) === false) {\n            <span>Normal Mode</span>\n          }\n        </button>\n      </span>\n    }\n", styles: [".translator-mode-button{background-color:red;border-radius:5px;border-style:outset;border-width:1px;color:#fff;font-weight:700;font-size:12px;width:100px;height:35px;padding:0}.depressed{border-style:inset}\n"] }]
    }], null, null); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(TranslationModeComponent, { className: "TranslationModeComponent", filePath: "lib/components/translation-mode/translation-mode.component.ts", lineNumber: 15 }); })();

// eslint-disable-next-line @angular-eslint/directive-selector
class TCTIf {
    constructor() {
        this._viewContainer = inject(ViewContainerRef);
        this.translatorViewService = inject(TranslatorViewService);
        this._context = new TCTIfContext();
        this._thenTemplateRef = null;
        this._elseTemplateRef = null;
        this._thenViewRef = null;
        this._elseViewRef = null;
        this.showAllMessagesSubscription = null;
        this.showAllMessages = false;
        const templateRef = inject(TemplateRef);
        this._thenTemplateRef = templateRef;
    }
    ngOnInit() {
        this.showAllMessagesSubscription =
            this.translatorViewService.showAllMessages$.subscribe((showAllMessages) => {
                this.showAllMessages = showAllMessages;
                this._updateView();
            });
    }
    ngOnDestroy() {
        this.showAllMessagesSubscription?.unsubscribe();
    }
    /**
     * The Boolean expression to evaluate as the condition for showing a template.
     */
    set tctIf(condition) {
        this._context.$implicit = this._context.tctIf = condition;
        this._updateView();
    }
    /**
     * A template to show if the condition expression evaluates to true.
     */
    set tctIfThen(templateRef) {
        assertTemplate('tctIfThen', templateRef);
        this._thenTemplateRef = templateRef;
        this._thenViewRef = null; // clear previous view if any.
        this._updateView();
    }
    /**
     * A template to show if the condition expression evaluates to false.
     */
    set tctIfElse(templateRef) {
        assertTemplate('tctIfElse', templateRef);
        this._elseTemplateRef = templateRef;
        this._elseViewRef = null; // clear previous view if any.
        this._updateView();
    }
    _updateView() {
        if (this.showAllMessages) {
            this._viewContainer.clear();
            if (this._thenTemplateRef) {
                this._thenViewRef = this._viewContainer.createEmbeddedView(this._thenTemplateRef, this._context);
            }
            if (this._elseTemplateRef) {
                this._elseViewRef = this._viewContainer.createEmbeddedView(this._elseTemplateRef, this._context);
            }
        }
        else {
            this._viewContainer.clear();
            if (this._context.$implicit) {
                this._elseViewRef = null;
                if (this._thenTemplateRef) {
                    this._thenViewRef = this._viewContainer.createEmbeddedView(this._thenTemplateRef, this._context);
                }
            }
            else {
                this._thenViewRef = null;
                if (this._elseTemplateRef) {
                    this._elseViewRef = this._viewContainer.createEmbeddedView(this._elseTemplateRef, this._context);
                }
            }
        }
    }
    /**
     * Asserts the correct type of the context for the template that `NgIf` will render.
     *
     * The presence of this method is a signal to the Ivy template type-check compiler that the
     * `NgIf` structural directive renders its template with a specific context type.
     */
    static ngTemplateContextGuard(dir, ctx) {
        return true;
    }
    static { this.ɵfac = function TCTIf_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || TCTIf)(); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: TCTIf, selectors: [["", "tctIf", ""]], inputs: { tctIf: "tctIf", tctIfThen: "tctIfThen", tctIfElse: "tctIfElse" } }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TCTIf, [{
        type: Directive,
        args: [{ standalone: true, selector: '[tctIf]' }]
    }], () => [], { tctIf: [{
            type: Input
        }], tctIfThen: [{
            type: Input
        }], tctIfElse: [{
            type: Input
        }] }); })();
/**
 * @publicApi
 */
class TCTIfContext {
    constructor() {
        this.$implicit = null;
        this.tctIf = null;
    }
}
function assertTemplate(property, templateRef) {
    const isTemplateRefOrNull = !!(!templateRef || templateRef.createEmbeddedView);
    if (!isTemplateRefOrNull) {
        throw new Error(`${property} must be a TemplateRef, but received '${_stringify(templateRef)}'.`);
    }
}

class SwitchView {
    constructor(_viewContainerRef, _templateRef) {
        this._viewContainerRef = _viewContainerRef;
        this._templateRef = _templateRef;
        this._created = false;
    }
    create() {
        this._created = true;
        this._viewContainerRef.createEmbeddedView(this._templateRef);
    }
    destroy() {
        this._created = false;
        this._viewContainerRef.clear();
    }
    enforceState(created) {
        if (created && !this._created) {
            this.create();
        }
        else if (!created && this._created) {
            this.destroy();
        }
    }
}
// eslint-disable-next-line @angular-eslint/directive-selector
class TCTSwitch {
    constructor() {
        this.translatorViewService = inject(TranslatorViewService);
        this._defaultViews = [];
        this._defaultUsed = false;
        this._caseCount = 0;
        this._lastCaseCheckIndex = 0;
        this._lastCasesMatched = false;
        this.showAllMessagesSubscription = null;
        this.showAllMessages = false;
    }
    ngOnInit() {
        this.showAllMessagesSubscription =
            this.translatorViewService.showAllMessages$.subscribe((showAllMessages) => {
                this.showAllMessages = showAllMessages;
            });
    }
    ngOnDestroy() {
        this.showAllMessagesSubscription?.unsubscribe();
    }
    set tctSwitch(newValue) {
        this._tctSwitch = newValue;
        if (this._caseCount === 0) {
            this.updateDefaultCases(true);
        }
    }
    /** @internal */
    addCase() {
        return this._caseCount++;
    }
    /** @internal */
    addDefault(view) {
        this._defaultViews.push(view);
    }
    /** @internal */
    matchCase(value) {
        const matched = value === this._tctSwitch || this.showAllMessages;
        this._lastCasesMatched = this._lastCasesMatched || matched;
        this._lastCaseCheckIndex++;
        if (this._lastCaseCheckIndex === this._caseCount) {
            this.updateDefaultCases(!this._lastCasesMatched || this.showAllMessages);
            this._lastCaseCheckIndex = 0;
            this._lastCasesMatched = false;
        }
        return matched;
    }
    updateDefaultCases(useDefault) {
        if (useDefault !== this._defaultUsed) {
            this._defaultUsed = useDefault;
            this._defaultViews.forEach((defaultView) => defaultView.enforceState(useDefault));
        }
    }
    static { this.ɵfac = function TCTSwitch_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || TCTSwitch)(); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: TCTSwitch, selectors: [["", "tctSwitch", ""]], inputs: { tctSwitch: "tctSwitch" } }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TCTSwitch, [{
        type: Directive,
        args: [{ standalone: true, selector: '[tctSwitch]' }]
    }], null, { tctSwitch: [{
            type: Input
        }] }); })();
// eslint-disable-next-line @angular-eslint/directive-selector
class TCTSwitchCase {
    constructor() {
        this.tctSwitch = inject(TCTSwitch, { host: true });
        const viewContainer = inject(ViewContainerRef);
        const templateRef = inject(TemplateRef);
        const tctSwitch = this.tctSwitch;
        tctSwitch.addCase();
        this.view = new SwitchView(viewContainer, templateRef);
    }
    /**
     * Performs case matching. For internal use only.
     */
    ngDoCheck() {
        this.view.enforceState(this.tctSwitch.matchCase(this.tctSwitchCase));
    }
    static { this.ɵfac = function TCTSwitchCase_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || TCTSwitchCase)(); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: TCTSwitchCase, selectors: [["", "tctSwitchCase", ""]], inputs: { tctSwitchCase: "tctSwitchCase" } }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TCTSwitchCase, [{
        type: Directive,
        args: [{ standalone: true, selector: '[tctSwitchCase]' }]
    }], () => [], { tctSwitchCase: [{
            type: Input
        }] }); })();
// eslint-disable-next-line @angular-eslint/directive-selector
class TCTSwitchDefault {
    constructor() {
        const viewContainer = inject(ViewContainerRef);
        const templateRef = inject(TemplateRef);
        const tctSwitch = inject(TCTSwitch, { host: true });
        tctSwitch.addDefault(new SwitchView(viewContainer, templateRef));
    }
    static { this.ɵfac = function TCTSwitchDefault_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || TCTSwitchDefault)(); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: TCTSwitchDefault, selectors: [["", "tctSwitchDefault", ""]] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TCTSwitchDefault, [{
        type: Directive,
        args: [{ standalone: true, selector: '[tctSwitchDefault]' }]
    }], () => [], null); })();

const TRANSLATION_DECLARATIONS = [
    TranslationDirective,
    PlaceHolderTranslationDirective,
    AriaLabelTranslationDirective,
    TCTIf,
    TCTSwitch,
    TCTSwitchCase,
    TCTSwitchDefault,
    TranslationModeComponent,
];

class TranslatorModule {
    static { this.ɵfac = function TranslatorModule_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || TranslatorModule)(); }; }
    static { this.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: TranslatorModule }); }
    static { this.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({}); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TranslatorModule, [{
        type: NgModule,
        args: [{
                imports: [...TRANSLATION_DECLARATIONS],
                exports: [...TRANSLATION_DECLARATIONS],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(TranslatorModule, { imports: [TranslationDirective, PlaceHolderTranslationDirective, AriaLabelTranslationDirective, TCTIf, TCTSwitch, TCTSwitchCase, TCTSwitchDefault, TranslationModeComponent], exports: [TranslationDirective, PlaceHolderTranslationDirective, AriaLabelTranslationDirective, TCTIf, TCTSwitch, TCTSwitchCase, TCTSwitchDefault, TranslationModeComponent] }); })();

/**
 * Configures translation services and features for the application.
 * This is the modern standalone API approach to setting up the @tc/translator library.
 *
 * Use this function in your application config to provide translation services with optional features.
 *
 * @param features - Feature providers from forGroup(), withRetry(), withCustomEndpoints()
 * @returns Environment providers for translation functionality
 *
 * @example
 * ```typescript
 * import { ApplicationConfig } from '@angular/core';
 * import { provideTranslation, forGroup, withRetry } from '@tc/translator';
 *
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     provideTranslation(
 *       forGroup('App', 'translation-indicator'),
 *       withRetry({ maxAttempts: 5, exponentialBackoff: true })
 *     )
 *   ]
 * };
 * ```
 *
 * @example
 * ```typescript
 * // Multiple translation groups with different configurations
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     // Main app translations
 *     provideTranslation(
 *       forGroup('App', 'app-indicator')
 *     ),
 *     // Admin panel translations with custom endpoints
 *     provideTranslation(
 *       forGroup('Admin', 'admin-indicator'),
 *       withCustomEndpoints({ fetchEndpoint: '/api/admin/translations' })
 *     )
 *   ]
 * };
 * ```
 *
 * @example
 * ```typescript
 * // All features combined
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     provideTranslation(
 *       forGroup('App', 'translation-indicator'),
 *       withRetry({
 *         maxAttempts: 5,
 *         exponentialBackoff: true,
 *         initialDelayMs: 2000
 *       }),
 *       withCustomEndpoints({
 *         fetchEndpoint: '/api/v2/translations',
 *         upsertEndpoint: '/api/v2/translation/save'
 *       })
 *     )
 *   ]
 * };
 * ```
 */
function provideTranslation(...features) {
    const translationDirectiveImports = importProvidersFrom(TranslatorModule);
    const normalizedFeatures = features.reduce((acc, feature) => {
        if (Array.isArray(feature)) {
            return [...acc, ...feature];
        }
        return [...acc, feature];
    }, []);
    return makeEnvironmentProviders([
        translationDirectiveImports,
        ...normalizedFeatures,
    ]);
}

/**
 * Configures a translation service instance for a specific translation group.
 * Each translation group has its own isolated set of translations and service instance.
 *
 * @param groupName - The name of the translation group (e.g., 'App', 'Admin', 'PublicSite')
 * @param indicatorClass - CSS class to apply to translation indicators in translation mode
 * @returns Array of providers to configure the translation group
 *
 * @example
 * ```typescript
 * import { provideTranslation, forGroup } from '@tc/translator';
 *
 * // Basic setup
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     provideTranslation(
 *       forGroup('App', 'app-translation-indicator')
 *     )
 *   ]
 * };
 * ```
 *
 * @example
 * ```typescript
 * // Multiple translation groups
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     // Group 1: Main application translations
 *     provideTranslation(
 *       forGroup('App', 'app-translation-indicator')
 *     ),
 *     // Group 2: Admin panel translations
 *     provideTranslation(
 *       forGroup('Admin', 'admin-translation-indicator')
 *     )
 *   ]
 * };
 * ```
 *
 * @example
 * ```typescript
 * // With additional features
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     provideTranslation(
 *       forGroup('App', 'translation-indicator'),
 *       withRetry({ maxAttempts: 5 }),
 *       withCustomEndpoints({ fetchEndpoint: '/api/v2/translations' })
 *     )
 *   ]
 * };
 * ```
 */
function forGroup(groupName, indicatorClass) {
    return [
        TranslationService,
        {
            provide: TranslationGroupNameToken,
            useValue: groupName,
        },
        {
            provide: TranslationIndicatorClassToken,
            useValue: indicatorClass,
        },
    ];
}

/**
 * Generated bundle index. Do not edit.
 */

export { AriaLabelTranslationDirective, LanguageService, LanguageType, MockAriaLabelTranslationDirective, MockLanguageService, MockPlaceHolderTranslationDirective, MockTranslationDirective, MockTranslationService, MockTranslatorViewService, PlaceHolderTranslationDirective, SwitchView, TCTIf, TCTIfContext, TCTSwitch, TCTSwitchCase, TCTSwitchDefault, TRANSLATION_DECLARATIONS, TranslationDirective, TranslationGroupNameToken, TranslationIndicatorClassToken, TranslationModeComponent, TranslationResolver, TranslationService, TranslatorModule, TranslatorViewService, forGroup, provideTranslation };

